/**
 * Будує ієрархічне дерево категорій з плоского масиву.
 * @param {Array<object>} items - Плоский масив об'єктів категорій. Кожен об'єкт повинен мати 'id' та 'parent_id'.
 * @returns {Array<object>} Масив кореневих вузлів дерева, де кожен вузол містить масив дочірніх елементів `children`.
 */
export const buildCategoryTree = (items) => {
  // Створюємо мапу для швидкого доступу до кожного елемента за його id
  const map = {};
  items.forEach((item) => {
    map[item.id] = { ...item, children: [] };
  });

  // Проходимо по вихідному масиву, щоб розставити елементи по місцях
  const tree = [];
  items.forEach((item) => {
    // Якщо у елемента є батько (parent_id), і цей батько існує в нашій мапі...
    if (item.parent_id && map[item.parent_id]) {
      // ...додаємо поточний елемент у масив 'children' його батька
      map[item.parent_id].children.push(map[item.id]);
    } else {
      // Інакше, це кореневий елемент (немає батька), додаємо його в основу дерева
      tree.push(map[item.id]);
    }
  });
  return tree;
};

/**
 * Перетворює ієрархічне дерево категорій назад у плоский масив,
 * додаючи відступи для візуального відображення ієрархії.
 * @param {Array<object>} nodes - Масив вузлів дерева (результат роботи buildCategoryTree).
 * @param {number} [level=0] - Поточний рівень вкладеності (використовується для рекурсії).
 * @returns {Array<object>} Плоский масив, відсортований відповідно до ієрархії,
 * де властивість 'name' містить відступи.
 */
export function flattenCategoryTree(nodes, level = 0) {
  // Використовуємо flatMap для рекурсивного обходу дерева та "розгладження" результату в один масив
  return nodes.flatMap((node) => {
    // Створюємо відступ з нерозривних пробілів, пропорційний рівню вкладеності
    const indent = "\u00A0".repeat(level * 4);
    // Формуємо новий об'єкт для плоского списку

    // Якщо у вузла є дочірні елементи...
    const option = { id: node.id, name: `${indent}${node.name}` };
    if (node.children && node.children.length) {
      // ...повертаємо сам вузол і рекурсивно викликаємо цю ж функцію для його дітей,
      // збільшуючи рівень вкладеності на 1
      return [option, ...flattenCategoryTree(node.children, level + 1)];
    }

    // Якщо дочірніх елементів немає, просто повертаємо поточний вузол
    return [option];
  });
}
